import {useNavigation} from '@react-navigation/native';
import {isEqual} from 'lodash';
import {LedApi3LightDto, MoodLightCategory} from 'puffco-api-axios-client';
import React from 'react';
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputChangeEventData,
  TextInputFocusEventData,
  View,
} from 'react-native';
import {useSelector} from 'react-redux';
import {ulid} from 'ulid';

import {Alert} from '../../components/Alert';
import {Button} from '../../components/Button';
import {RoundColorPicker} from '../../components/RoundColorPicker';
import {Constants} from '../../constants';
import {Alerts, Strings} from '../../constants/Strings';
import {Screens} from '../../constants/navigation';
import {useSpinner} from '../../contexts/spinner';
import {useRealtimeUnitLighting} from '../../contexts/unit-lighting/useRealtimeUnitLight';
import {CheckIcon, PencilIcon} from '../../icons';
import {MoodLightEvaluator} from '../../lib/ble2/v2/MoodLightEvaluator';
import {useAdaptiveSafeArea} from '../../lib/hooks/useAdaptiveSafeArea';
import {useBackPress} from '../../lib/hooks/useBackPress';
import {useDisconnectGuard} from '../../lib/hooks/useDisconnectGuard';
import {useHasLed3Api} from '../../lib/hooks/useHasLed3Api';
import {useSaveMoodLight} from '../../lib/hooks/useSaveMoodLight';
import {useThrottle} from '../../lib/hooks/useThrottle';
import {useWindowHeight, useWindowWidth} from '../../lib/hooks/useWindowHeight';
import {LED3Data} from '../../lib/led3/led3data';
import {
  connectedPeakSelector,
  currentDeviceHeatProfilesSelector,
} from '../../lib/redux/slices/bleSlice';
import {allAndCurrentDeviceMoodLightsSelector} from '../../lib/redux/slices/moodLightSlice';
import {MoodLight, MoodType, isMoodLightId} from '../../lib/types';
import styled from '../../lib/utils/styled';
import {toHeatCycle} from '../../lib/utils/toHeatCycle';
import type {MoodLightControllerNavigatorScreenProps} from '../../navigators/MainStackNavigator';
import {colors, fillStyle} from '../../styles';
import {DraggableBottomSheet as MoodLightEditor} from '../components';
import {ControlPanelComponent} from './components/ControlPanelComponent';

interface Props {
  id?: string;
  isFromEditProfile?: boolean;
  onSave: () => void;
}

type Navigation = MoodLightControllerNavigatorScreenProps<
  typeof Screens.MoodLightCreate | typeof Screens.MoodLightEdit
>['navigation'];

type Values = {[x: string]: any};

const getInitialAnimation = (hasLed3Api: boolean) =>
  hasLed3Api ? MoodType.LAVA_LAMP : MoodType.DISCO;

export const MoodLightForm = ({id = '', onSave}: Props) => {
  useDisconnectGuard();

  const navigation = useNavigation<Navigation>();
  const {windowHeight: height} = useWindowHeight();
  const {windowWidth: width} = useWindowWidth();
  const {bottom} = useAdaptiveSafeArea();
  const peak = useSelector(connectedPeakSelector);
  const deviceHeatProfiles = useSelector(currentDeviceHeatProfilesSelector);
  const allMoodLights = useSelector(allAndCurrentDeviceMoodLightsSelector);

  const colorPickerContainerDiameter = (width / 2 + height / 2) / 2;

  const isEditing = id !== '';
  const nameRef = React.useRef<TextInput>(null);

  const hasLed3Api = useHasLed3Api();

  const moodLight = React.useMemo<MoodLight | undefined>(
    () => allMoodLights[id],
    [allMoodLights, id],
  );

  const getInitialStateValues = (name: string, moodLight?: MoodLight) => {
    const type: MoodType =
      moodLight?.projector.meta.moodType ?? getInitialAnimation(hasLed3Api);

    const light = LED3Data[type];

    const values = MoodLightEvaluator.buildEvaluationContext(light, {
      ...light.projector.meta,
      ...(moodLight?.projector.meta ?? {}),
    });

    return {
      light: {...light, name},
      values: {...values, userColors: values.userColors, moodType: type},
    };
  };

  const initialName = moodLight?.name ?? 'NEW MOOD';

  const initialStateValues = getInitialStateValues(initialName, moodLight);

  const [name, setName] = React.useState(initialName);
  const [state, setState] = React.useState<{
    light: LedApi3LightDto;
    values: Values;
  }>(initialStateValues);
  const [shouldOpen, setShouldOpen] = React.useState<boolean>(false);

  const onAnimationValueChange = (type: MoodType) => {
    const light = LED3Data[type];

    const values = MoodLightEvaluator.buildEvaluationContext(light, {
      ...light.projector.meta,
      ...state.values,
    });

    const newState = {
      light: {...light, name},
      values: {...values, moodType: type},
    };

    setState(newState);
  };

  const onBackPress = React.useCallback(() => {
    navigation.goBack();

    return true;
  }, [navigation]);

  useBackPress(onBackPress);

  const onMoodlightNameInputChange = (
    event: NativeSyntheticEvent<TextInputChangeEventData>,
  ) => {
    setName(event.nativeEvent.text);
  };

  const onMoodlightNameInputBlur = (
    event: NativeSyntheticEvent<TextInputFocusEventData>,
  ) => {
    if (shouldOpen) {
      nameRef?.current?.focus();
      setShouldOpen(false);
    }

    setName(event.nativeEvent.text);
  };

  const updateColors = React.useCallback((colors: string[]) => {
    setState(previous => ({
      ...previous,
      values: {...previous.values, userColors: colors},
    }));
  }, []);

  const throttledUpdateColor = useThrottle(
    updateColors,
    Constants.MOOD_LIGHT_WRITING_DELAY.THROTTLE,
  );

  const onMoodLightColorPickerValueChange = (colors: string[]) => {
    throttledUpdateColor(colors);
  };

  const onMoodLightColorPickerValueChangeRelease = (colors: string[]) => {
    updateColors(colors);
  };

  const onMoodLightValuesChange = (values: Values) => {
    setState(previous => ({...previous, values}));
  };

  const lantern = React.useMemo<MoodLight>(
    () => ({
      category: moodLight?.category ?? MoodLightCategory.Custom,
      format: 'led-api-3',
      created: new Date().toISOString(),
      ...state.light,
      id: moodLight?.id ?? ulid(),
      name,
      modified: new Date().toISOString(),
      projector: {
        ...state.light.projector,
        meta: {...state.light.projector.meta, ...state.values},
      },
    }),
    [name, moodLight?.id, moodLight?.category, state.light, state.values],
  );

  useRealtimeUnitLighting(
    isEditing ? Screens.MoodLightEdit : Screens.MoodLightCreate,
    lantern,
  );

  const [{loading: isSaving}, save] = useSaveMoodLight({
    onSuccess: React.useCallback(() => navigation.goBack(), []),
    onError: React.useCallback(
      (error: Error) =>
        Alert.alert(
          isEditing
            ? Alerts.MOOD_LIGHT_SAVE_ERROR_TITLE
            : Alerts.MOOD_LIGHT_CREATE_ERROR_TITLE,
          error.message,
        ),
      [isEditing],
    ),
  });

  const haveValuesChanged =
    !isEqual(initialStateValues.values, state.values) || initialName !== name;

  const saveCustomMoodLight = React.useCallback(async () => {
    const updated = {
      ...state.light,
      id: moodLight?.id ?? ulid(),
      name,
      modified: new Date().toISOString(),
      projector: {
        ...state.light.projector,
        meta: {...state.light.projector.meta, ...state.values},
      },
    };

    const result = await save(updated);

    if (!result) {
      onSave();

      return;
    }

    const toModify = deviceHeatProfiles.filter(
      profile => isMoodLightId(profile.light) && profile.light.id === result.id,
    );

    await peak?.cycles.writeHeatCycles(
      toModify.map(item => toHeatCycle(item, () => result)),
    );

    onSave();
  }, [
    name,
    peak,
    deviceHeatProfiles,
    moodLight,
    state.light,
    state.values,
    save,
    onSave,
  ]);

  useSpinner({
    color: colors.white,
    isVisible: isSaving,
    text: Strings.SAVING,
  });

  React.useEffect(() => {
    navigation.setOptions({
      ...((!isEditing || haveValuesChanged) && {
        headerRight: () => (
          <Button
            variant="transparent"
            onPress={saveCustomMoodLight}
            disabled={isSaving}>
            <CheckIcon size={28} color={colors.white} />
          </Button>
        ),
      }),
    });
  }, [isSaving, isEditing, haveValuesChanged, saveCustomMoodLight, navigation]);

  const navigateToAnimation = () => {
    navigation.navigate(Screens.MoodLightAnimation, {
      onAnimationValueChange,
      initialAnimationValue: state.values.moodType ?? MoodType.NO_ANIMATION,
    });
  };

  const widgets = state.light.ui;

  return (
    <ScreenContainer
      style={{backgroundColor: colors.black, paddingBottom: bottom}}>
      <MoodLightEditor
        header={Strings.CONTROL_PANEL}
        bottomContainer={
          <ControlPanelComponent
            widgets={widgets}
            moodLightValues={state.values}
            setMoodLightValues={onMoodLightValuesChange}
            animation={state.values.moodType}
            navigateTo={navigateToAnimation}
          />
        }
        bottomSheetStyle={{flex: 1, width: '100%'}}>
        <UpperContainer>
          <TitleContainer>
            <TextInput
              allowFontScaling={false}
              autoCapitalize="characters"
              ref={nameRef}
              value={name}
              keyboardType={
                Constants.IS_NATIVE_ANDROID ? 'visible-password' : 'default'
              }
              maxLength={Constants.PROFILE_NAME_MAX_LENGTH}
              onChange={onMoodlightNameInputChange}
              onBlur={onMoodlightNameInputBlur}
              style={{
                fontSize: 24,
                color: colors.white,
                fontFamily: 'Roboto-Bold',
                fontWeight: '400',
                lineHeight: 26,
                textTransform: 'uppercase',
                textAlign: 'center',
                width: '80%',
                marginRight: 6,
              }}
            />

            <PencilIcon
              style={{width: 11, top: Constants.IS_WEB ? 0 : -2}}
              color={colors.white}
              size={16}
              onPress={() => {
                nameRef.current?.focus();
                Constants.IS_WEB && setShouldOpen(true);
              }}
            />
          </TitleContainer>
        </UpperContainer>

        <MoodLightPickerContainer>
          <RoundColorPicker
            diameter={colorPickerContainerDiameter}
            onValueChange={onMoodLightColorPickerValueChange}
            onValueChangeRelease={onMoodLightColorPickerValueChangeRelease}
            colors={state.values.userColors}
          />
        </MoodLightPickerContainer>
      </MoodLightEditor>
    </ScreenContainer>
  );
};

const ScreenContainer = styled(View)({
  ...fillStyle,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
});

const UpperContainer = styled(View)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%',
  flex: 0.6,
});

const TitleContainer = styled(View)({
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: 40,
  padding: 10,
});

const MoodLightPickerContainer = styled(View)({
  display: 'flex',
  flex: 1.5,
  alignItems: 'center',
  justifyContent: 'center',
});
